import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Paper, Typography, Button, CircularProgress, Alert, Stack,
} from '@mui/material';
import { Print, PictureAsPdf, GridOn, Refresh } from '@mui/icons-material';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { reportReceiptsPaymentsURL } from '../../BaseURL';

function headers() { return { Authorization: `Bearer ${localStorage.getItem('church_token')}` }; }

const fmt = (n) =>
  Number(n || 0).toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDateLong = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString('en-GH', { day: '2-digit', month: 'long', year: 'numeric' });
};

const NAVY = '#0B2545';
const RED  = '#B91C1C';
const RULE = '#0B2545';

export default function ReceiptsPayments({ start, end }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const printRef = useRef(null);

  const load = () => {
    if (!start || !end) return;
    setLoading(true); setError('');
    axios.get(reportReceiptsPaymentsURL, { headers: headers(), params: { start, end } })
      .then(r => { if (r.data.success) setData(r.data); else setError(r.data.message); })
      .catch(err => setError(err.response?.data?.message || err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [start, end]);

  // ─── PRINT ────────────────────────────────────────────────────────────────
  const handlePrint = () => {
    if (!printRef.current) return;
    const w = window.open('', '_blank', 'width=1080,height=820');
    if (!w) return;
    w.document.write(`
      <html><head><title>Receipts &amp; Payment Account - ${data?.period.label || ''}</title>
      <style>
        * { box-sizing: border-box; }
        body { font-family: 'Helvetica Neue', -apple-system, Segoe UI, Roboto, Arial, sans-serif; margin: 28px 32px; color: #111; }
        .letterhead { display: flex; align-items: center; gap: 20px; padding-bottom: 14px; border-bottom: 3px double ${NAVY}; }
        .letterhead img { width: 84px; height: 84px; object-fit: contain; border-radius: 6px; }
        .letterhead .info h1 { margin: 0; font-size: 26px; letter-spacing: 0.02em; color: ${NAVY}; }
        .letterhead .info p { margin: 3px 0 0; color: #555; font-size: 12px; }
        .stmt-title { text-align: center; margin: 22px 0 4px; }
        .stmt-title .name { font-size: 15px; font-weight: 800; letter-spacing: 0.16em; color: ${NAVY}; border-top: 1.5px solid ${NAVY}; border-bottom: 1.5px solid ${NAVY}; display: inline-block; padding: 4px 24px; }
        .stmt-title .period { margin-top: 8px; font-size: 12px; color: #555; }
        table { width: 100%; border-collapse: collapse; margin-top: 18px; }
        thead th { text-align: right; font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; color: ${RED}; padding: 6px 12px; font-weight: 700; }
        thead th.left { text-align: left; color: #333; }
        tbody td { padding: 5px 12px; font-size: 12.5px; }
        tbody td.right { text-align: right; font-variant-numeric: tabular-nums; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
        .heading td { font-weight: 700; color: ${NAVY}; padding: 8px 12px; text-decoration: underline; }
        .band td { background: #F5EFE4; color: ${NAVY}; font-weight: 700; padding: 6px 12px; text-transform: uppercase; letter-spacing: 0.06em; }
        .item td { padding-left: 20px; color: #1e293b; }
        .num-under td { border-bottom: 1px solid #333; padding: 2px 12px 4px; }
        .subtotal td { font-weight: 700; }
        .grand td { font-weight: 800; color: ${NAVY}; border-top: 1px solid #333; border-bottom: 3px double ${NAVY}; padding: 6px 12px; }
        .signatures { display: flex; gap: 40px; justify-content: space-between; margin-top: 60px; page-break-inside: avoid; }
        .sig { flex: 1; text-align: center; }
        .sig .line { border-top: 1px solid #333; margin-bottom: 6px; }
        .sig .role { font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; color: #555; font-weight: 700; }
        .footer { display: flex; justify-content: space-between; margin-top: 30px; font-size: 10px; color: #888; }
        @page { size: A4; margin: 15mm; }
      </style>
      </head><body>${printRef.current.innerHTML}</body></html>
    `);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); w.close(); }, 300);
  };

  // ─── EXPORT PDF ───────────────────────────────────────────────────────────
  const handlePdf = () => {
    if (!data) return;
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 42;

    // Letterhead
    let y = 40;
    const logoSize = 60;
    let textLeft = margin;
    if (data.church?.logo && String(data.church.logo).startsWith('data:image')) {
      try {
        const fmtType = data.church.logo.substring(11, data.church.logo.indexOf(';'));
        doc.addImage(data.church.logo, fmtType.toUpperCase(), margin, y, logoSize, logoSize);
        textLeft = margin + logoSize + 14;
      } catch (e) { /* ignore */ }
    }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(20); doc.setTextColor(11, 37, 69);
    doc.text(data.church?.name || 'Church', textLeft, y + 20);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(90);
    const subInfo = [data.church?.address, data.church?.phone, data.church?.email].filter(Boolean).join('  ·  ');
    if (subInfo) doc.text(subInfo, textLeft, y + 36);
    y += Math.max(logoSize, 42) + 8;
    doc.setDrawColor(11, 37, 69); doc.setLineWidth(1.2);
    doc.line(margin, y, pageW - margin, y);
    doc.setLineWidth(0.4); doc.line(margin, y + 3, pageW - margin, y + 3);
    y += 20;

    // Title
    doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(11, 37, 69);
    const title = 'RECEIPTS & PAYMENT ACCOUNT';
    const titleW = doc.getTextWidth(title);
    const tx = (pageW - titleW) / 2;
    doc.setLineWidth(0.8);
    doc.line(tx - 18, y - 4, tx + titleW + 18, y - 4);
    doc.text(title, pageW / 2, y + 12, { align: 'center' });
    doc.line(tx - 18, y + 18, tx + titleW + 18, y + 18);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(90);
    doc.text(
      `For the financial period ${fmtDateLong(data.period.start_date)} to ${fmtDateLong(data.period.end_date)}`,
      pageW / 2, y + 34, { align: 'center' }
    );
    y += 46;

    // Body
    const body = [];
    const CENTER = [244, 240, 232];
    const NAVY_RGB = [11, 37, 69];

    const heading = (t) => body.push([
      { content: t, styles: { fontStyle: 'bold', textColor: NAVY_RGB } }, '', '',
    ]);
    const band = (t) => body.push([
      { content: t, colSpan: 3, styles: { fillColor: CENTER, textColor: NAVY_RGB, fontStyle: 'bold' } },
    ]);
    const line = (name, amt1, amt2, opts = {}) => body.push([
      { content: opts.indent === false ? name : `   ${name}`, styles: opts.labelStyles || {} },
      { content: amt1 == null ? '' : fmt(amt1), styles: { halign: 'right', ...(opts.amt1Styles || {}) } },
      { content: amt2 == null ? '' : fmt(amt2), styles: { halign: 'right', ...(opts.amt2Styles || {}) } },
    ]);

    // OPENING
    heading('Opening Cash Bal:');
    data.opening.cash.forEach(c => line(c.name, c.amount));
    line('Total Cash b/fwd', null, data.opening.total_cash, {
      indent: false,
      labelStyles: { fontStyle: 'bold' },
      amt2Styles: { fontStyle: 'bold' },
    });

    heading('Opening Bank Bal:');
    data.opening.banks.forEach(b => line(b.name, b.balance));
    line('Total Bank b/fwd:', null, data.opening.total_banks, {
      indent: false,
      labelStyles: { fontStyle: 'bold' },
      amt2Styles: { fontStyle: 'bold' },
    });

    // RECEIPTS (grouped by category)
    heading('RECEIPTS:');
    data.receipt_categories.forEach(cat => {
      band(cat.name);
      cat.items.forEach(it => line(it.name, it.amount));
      line('', null, cat.total, {
        indent: false,
        amt2Styles: { fontStyle: 'bold' },
      });
    });
    line('TOTAL RECEIPTS', null, data.total_receipts, {
      indent: false,
      labelStyles: { fontStyle: 'bold' },
      amt2Styles: { fontStyle: 'bold' },
    });
    body.push([
      { content: '', styles: {} },
      { content: '', styles: {} },
      { content: fmt(data.grand_receipts), styles: { halign: 'right', fontStyle: 'bold', textColor: NAVY_RGB } },
    ]);

    // PAYMENTS
    heading('LESS PAYMENT');
    data.payment_categories.forEach(cat => {
      band(cat.name);
      cat.items.forEach(it => line(it.name, it.amount));
      line('', null, cat.total, {
        indent: false,
        amt2Styles: { fontStyle: 'bold' },
      });
    });
    line('Total Payment', null, data.total_payments, {
      indent: false,
      labelStyles: { fontStyle: 'bold' },
      amt2Styles: { fontStyle: 'bold' },
    });

    // CLOSING
    heading('Closing Cash Bal:');
    data.closing.cash.forEach(c => line(c.name, c.amount));
    line('Total Cash-In-Hand', null, data.closing.total_cash, {
      indent: false,
      labelStyles: { fontStyle: 'bold' },
      amt2Styles: { fontStyle: 'bold' },
    });

    heading('Closing Bank Bal:');
    data.closing.banks.forEach(b => line(b.name, b.balance));
    line('', null, data.closing.total_banks, {
      indent: false,
      amt2Styles: { fontStyle: 'bold' },
    });

    // Grand
    body.push([
      { content: 'GRAND TOTAL', styles: { fontStyle: 'bold', textColor: NAVY_RGB } },
      { content: '', styles: {} },
      { content: fmt(data.reconciliation.grand_payments), styles: { halign: 'right', fontStyle: 'bold', textColor: NAVY_RGB } },
    ]);

    autoTable(doc, {
      startY: y,
      head: [[
        '',
        { content: 'GH₵', styles: { halign: 'right', textColor: [185, 28, 28] } },
        { content: 'GH₵', styles: { halign: 'right', textColor: [185, 28, 28] } },
      ]],
      body,
      theme: 'plain',
      styles: { fontSize: 9.5, cellPadding: 4 },
      headStyles: { fontStyle: 'bold' },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 110, halign: 'right' },
        2: { cellWidth: 110, halign: 'right' },
      },
      margin: { top: 40, left: margin, right: margin, bottom: 100 },
      didDrawPage: () => {
        doc.setFontSize(9); doc.setTextColor(120);
        doc.text(`Page ${doc.internal.getNumberOfPages()}`, pageW - margin, pageH - 24, { align: 'right' });
        doc.text(`Generated ${new Date().toLocaleString('en-GH')}`, margin, pageH - 24);
      },
    });

    // Signatures
    let sy = doc.lastAutoTable?.finalY || y;
    sy += 44;
    if (sy > pageH - 110) { doc.addPage(); sy = 100; }
    const roles = ['Prepared by', 'Reviewed by', 'Approved by'];
    const colW  = (pageW - margin * 2) / roles.length;
    roles.forEach((role, i) => {
      const cx = margin + colW * i + 20;
      const cw = colW - 40;
      doc.setDrawColor(60); doc.setLineWidth(0.6);
      doc.line(cx, sy, cx + cw, sy);
      doc.setFontSize(9); doc.setTextColor(80); doc.setFont('helvetica', 'bold');
      doc.text(role.toUpperCase(), cx + cw / 2, sy + 14, { align: 'center' });
      doc.setFont('helvetica', 'normal'); doc.setTextColor(120);
      doc.text('Name & Signature', cx + cw / 2, sy + 28, { align: 'center' });
    });

    const stamp = `${data.period.start_date || start}_to_${data.period.end_date || end}`;
    doc.save(`Receipts_Payments_${stamp.replace(/[\/\s]/g, '_')}.pdf`);
  };

  // ─── EXPORT EXCEL ─────────────────────────────────────────────────────────
  const handleExcel = () => {
    if (!data) return;
    const rows = [];
    rows.push([data.church?.name || 'Church']);
    if (data.church?.address) rows.push([data.church.address]);
    const contact = [data.church?.phone, data.church?.email].filter(Boolean).join(' · ');
    if (contact) rows.push([contact]);
    rows.push([]);
    rows.push(['RECEIPTS & PAYMENT ACCOUNT']);
    rows.push([`For the financial period ${fmtDateLong(data.period.start_date)} to ${fmtDateLong(data.period.end_date)}`]);
    rows.push([]);
    rows.push(['', 'GH₵', 'GH₵']);

    rows.push(['Opening Cash Bal:']);
    data.opening.cash.forEach(c => rows.push([`   ${c.name}`, Number(c.amount)]));
    rows.push(['Total Cash b/fwd', '', Number(data.opening.total_cash)]);

    rows.push(['Opening Bank Bal:']);
    data.opening.banks.forEach(b => rows.push([`   ${b.name}`, Number(b.balance)]));
    rows.push(['Total Bank b/fwd:', '', Number(data.opening.total_banks)]);

    rows.push(['RECEIPTS:']);
    data.receipt_categories.forEach(cat => {
      rows.push([cat.name]);
      cat.items.forEach(it => rows.push([`   ${it.name}`, Number(it.amount)]));
      rows.push(['', '', Number(cat.total)]);
    });
    rows.push(['TOTAL RECEIPTS', '', Number(data.total_receipts)]);
    rows.push(['', '', Number(data.grand_receipts)]);

    rows.push(['LESS PAYMENT']);
    data.payment_categories.forEach(cat => {
      rows.push([cat.name]);
      cat.items.forEach(it => rows.push([`   ${it.name}`, Number(it.amount)]));
      rows.push(['', '', Number(cat.total)]);
    });
    rows.push(['Total Payment', '', Number(data.total_payments)]);

    rows.push(['Closing Cash Bal:']);
    data.closing.cash.forEach(c => rows.push([`   ${c.name}`, Number(c.amount)]));
    rows.push(['Total Cash-In-Hand', '', Number(data.closing.total_cash)]);

    rows.push(['Closing Bank Bal:']);
    data.closing.banks.forEach(b => rows.push([`   ${b.name}`, Number(b.balance)]));
    rows.push(['', '', Number(data.closing.total_banks)]);

    rows.push([]);
    rows.push(['GRAND TOTAL', '', Number(data.reconciliation.grand_payments)]);

    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 44 }, { wch: 18 }, { wch: 18 }];
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = range.s.r; R <= range.e.r; R++) {
      for (let C = 1; C <= 2; C++) {
        const addr = XLSX.utils.encode_cell({ r: R, c: C });
        const cell = ws[addr];
        if (cell && typeof cell.v === 'number') cell.z = '#,##0.00';
      }
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Receipts & Payments');
    const stamp = `${data.period.start_date || start}_to_${data.period.end_date || end}`;
    XLSX.writeFile(wb, `Receipts_Payments_${stamp.replace(/[\/\s]/g, '_')}.xlsx`);
  };

  if (loading) {
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Building receipts &amp; payment account for {fmtDateLong(start)} — {fmtDateLong(end)}…
        </Typography>
      </Box>
    );
  }
  if (error) return <Alert severity="error" onClose={() => setError('')}>{error}</Alert>;
  if (!data) return null;

  const hasAnyData =
    data.opening.total_banks !== 0 || data.opening.total_cash !== 0 ||
    data.receipt_categories.length > 0 || data.payment_categories.length > 0 ||
    data.closing.total_banks !== 0 || data.closing.total_cash !== 0;

  const reconciled = data.reconciliation.reconciled;

  return (
    <Box>
      {/* Action bar */}
      <Stack direction="row" spacing={1} justifyContent="flex-end" mb={2} flexWrap="wrap">
        <Button size="small" variant="outlined" startIcon={<Refresh />} onClick={load}>Refresh</Button>
        <Button size="small" variant="outlined" startIcon={<Print />} onClick={handlePrint} disabled={!hasAnyData}>Print</Button>
        <Button size="small" variant="outlined" startIcon={<GridOn />} onClick={handleExcel} disabled={!hasAnyData}
          sx={{ color: '#0F6C3D', borderColor: '#0F6C3D', '&:hover': { borderColor: '#0F6C3D', bgcolor: '#0F6C3D0d' } }}>
          Export Excel
        </Button>
        <Button size="small" variant="contained" startIcon={<PictureAsPdf />} onClick={handlePdf} disabled={!hasAnyData}
          sx={{ bgcolor: RED, '&:hover': { bgcolor: '#991B1B' } }}>
          Export PDF
        </Button>
      </Stack>

      {!reconciled && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Reconciliation off by <b>GH₵ {fmt(Math.abs(data.reconciliation.difference))}</b> — receipts ({fmt(data.reconciliation.grand_receipts)}) ≠ payments &amp; closing balances ({fmt(data.reconciliation.grand_payments)}).
        </Alert>
      )}

      <Paper
        elevation={2}
        sx={{
          border: '1px solid #E2E8F0', borderRadius: 2,
          p: { xs: 3, md: 6 }, bgcolor: '#fff',
          maxWidth: 1040, mx: 'auto',
          boxShadow: '0 10px 30px rgba(15,23,42,0.06)',
        }}
      >
        <Box ref={printRef}>
          {/* Letterhead */}
          <Box className="letterhead" sx={{
            display: 'flex', alignItems: 'center', gap: 3,
            pb: 2, borderBottom: `3px double ${RULE}`,
          }}>
            {data.church?.logo ? (
              <Box component="img" src={data.church.logo} alt="Logo"
                sx={{ width: 90, height: 90, objectFit: 'contain', borderRadius: 1 }} />
            ) : (
              <Box sx={{
                width: 90, height: 90, borderRadius: 1,
                bgcolor: `${NAVY}12`, color: NAVY,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 800, fontSize: 30,
              }}>
                {(data.church?.name || 'C').charAt(0).toUpperCase()}
              </Box>
            )}
            <Box className="info" sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h4" fontWeight={800}
                sx={{ color: NAVY, letterSpacing: '0.02em', lineHeight: 1.1 }}>
                {data.church?.name || 'Church'}
              </Typography>
              {(data.church?.address || data.church?.phone || data.church?.email) && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {[data.church?.address, data.church?.phone, data.church?.email].filter(Boolean).join('  ·  ')}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Title */}
          <Box className="stmt-title" sx={{ textAlign: 'center', mt: 3.5, mb: 0.5 }}>
            <Typography className="name" sx={{
              fontSize: 15, fontWeight: 800, letterSpacing: '0.16em', color: NAVY,
              borderTop: `1.5px solid ${NAVY}`, borderBottom: `1.5px solid ${NAVY}`,
              display: 'inline-block', px: 3, py: 0.5,
            }}>
              RECEIPTS &amp; PAYMENT ACCOUNT
            </Typography>
            <Typography className="period" variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              For the financial period <b>{fmtDateLong(data.period.start_date)}</b> to <b>{fmtDateLong(data.period.end_date)}</b>
            </Typography>
          </Box>

          {/* Statement body */}
          <StatementTable data={data} />

          {/* Signatures */}
          {hasAnyData && (
            <Box className="signatures" sx={{
              display: 'flex', gap: { xs: 3, md: 5 }, mt: 8,
              justifyContent: 'space-between', pageBreakInside: 'avoid',
            }}>
              {['Prepared by', 'Reviewed by', 'Approved by'].map(role => (
                <Box key={role} className="sig" sx={{ flex: 1, textAlign: 'center' }}>
                  <Box className="line" sx={{ borderTop: '1px solid #333', mb: 0.8 }} />
                  <Typography className="role" sx={{
                    fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase',
                    color: '#555', fontWeight: 700,
                  }}>
                    {role}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#999' }}>Name &amp; Signature</Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* Footer */}
          <Box className="footer" sx={{
            display: 'flex', justifyContent: 'space-between', mt: 4,
            fontSize: 10, color: '#888',
          }}>
            <span>Generated {new Date().toLocaleString('en-GH')}</span>
            <span>{data.church?.name || ''}</span>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

// ─── Inner rendered table (two-money-column ledger layout) ────────────────
function StatementTable({ data }) {
  return (
    <table style={tableStyle}>
      <thead>
        <tr>
          <th style={{ ...thStyle, textAlign: 'left', color: '#333' }}></th>
          <th style={thStyle}>GH₵</th>
          <th style={thStyle}>GH₵</th>
        </tr>
      </thead>
      <tbody>
        {/* ── Opening cash ────────────────────────────────────────────── */}
        <HeadingRow label="Opening Cash Bal:" />
        {data.opening.cash.map((c, i) => (
          <ItemRow key={`oc-${i}`} label={c.name} left={c.amount} />
        ))}
        <SubtotalRow label="Total Cash b/fwd" right={data.opening.total_cash} />

        {/* ── Opening bank ────────────────────────────────────────────── */}
        <HeadingRow label="Opening Bank Bal:" />
        {data.opening.banks.map(b => (
          <ItemRow key={`ob-${b.id}`} label={b.name} left={b.balance} />
        ))}
        <SubtotalRow label="Total Bank b/fwd:" right={data.opening.total_banks} />

        {/* ── Receipts (grouped by category) ──────────────────────────── */}
        <HeadingRow label="RECEIPTS:" underline />
        {data.receipt_categories.length === 0 ? (
          <tr><td colSpan={3} style={{ ...itemStyle, color: '#999' }}>   No receipts in this period.</td></tr>
        ) : data.receipt_categories.map((cat, ci) => (
          <React.Fragment key={`rcat-${ci}`}>
            <tr>
              <td colSpan={3} style={bandStyle}>{cat.name}</td>
            </tr>
            {cat.items.map((it, i) => (
              <ItemRow key={`ri-${ci}-${i}`} label={it.name} left={it.amount} />
            ))}
            <SubtotalRow label="" right={cat.total} />
          </React.Fragment>
        ))}
        <SubtotalRow label="TOTAL RECEIPTS" right={data.total_receipts} bold />
        <tr>
          <td style={{ ...itemStyle, border: 'none' }}></td>
          <td style={{ ...amountStyle, border: 'none' }}></td>
          <td style={{
            ...amountStyle,
            fontWeight: 800, color: NAVY,
            borderTop: '1px solid #333', borderBottom: '3px double #333',
          }}>{fmt(data.grand_receipts)}</td>
        </tr>

        {/* Small breather */}
        <tr><td colSpan={3} style={{ height: 10 }} /></tr>

        {/* ── Payments ────────────────────────────────────────────────── */}
        <HeadingRow label="LESS PAYMENT" underline />
        {data.payment_categories.length === 0 ? (
          <tr><td colSpan={3} style={{ ...itemStyle, color: '#999' }}>   No payments in this period.</td></tr>
        ) : data.payment_categories.map((cat, ci) => (
          <React.Fragment key={`cat-${ci}`}>
            <tr>
              <td colSpan={3} style={bandStyle}>{cat.name}</td>
            </tr>
            {cat.items.map((it, i) => (
              <ItemRow key={`ci-${ci}-${i}`} label={it.name} left={it.amount} />
            ))}
            <SubtotalRow label="" right={cat.total} />
          </React.Fragment>
        ))}
        <SubtotalRow label="Total Payment" right={data.total_payments} bold />

        {/* ── Closing cash ────────────────────────────────────────────── */}
        <HeadingRow label="Closing Cash Bal:" />
        {data.closing.cash.map((c, i) => (
          <ItemRow key={`cc-${i}`} label={c.name} left={c.amount} />
        ))}
        <SubtotalRow label="Total Cash-In-Hand" right={data.closing.total_cash} bold />

        {/* ── Closing bank ────────────────────────────────────────────── */}
        <HeadingRow label="Closing Bank Bal:" />
        {data.closing.banks.map(b => (
          <ItemRow key={`cb-${b.id}`} label={b.name} left={b.balance} />
        ))}
        <SubtotalRow label="" right={data.closing.total_banks} bold />

        {/* ── Grand total ─────────────────────────────────────────────── */}
        <tr>
          <td style={{ ...itemStyle, fontWeight: 800, color: NAVY, paddingLeft: 12 }}>GRAND TOTAL</td>
          <td style={{ ...amountStyle, border: 'none' }}></td>
          <td style={{
            ...amountStyle,
            fontWeight: 800, color: NAVY,
            borderTop: '1px solid #333', borderBottom: '3px double #333',
          }}>{fmt(data.reconciliation.grand_payments)}</td>
        </tr>
      </tbody>
    </table>
  );
}

// ─── Row helpers ──────────────────────────────────────────────────────────
function HeadingRow({ label, underline }) {
  return (
    <tr>
      <td colSpan={3} style={{
        ...headingCellStyle,
        textDecoration: underline ? 'underline' : 'none',
      }}>{label}</td>
    </tr>
  );
}
function ItemRow({ label, left, right }) {
  return (
    <tr>
      <td style={itemStyle}>{label}</td>
      <td style={amountStyle}>{left != null ? fmt(left) : ''}</td>
      <td style={amountStyle}>{right != null ? fmt(right) : ''}</td>
    </tr>
  );
}
function SubtotalRow({ label, right, bold }) {
  return (
    <tr>
      <td style={{ ...itemStyle, fontWeight: bold ? 800 : 700, color: NAVY, paddingLeft: 12 }}>
        {label}
      </td>
      <td style={{ ...amountStyle, borderTop: '1px solid #333' }}></td>
      <td style={{
        ...amountStyle,
        borderTop: '1px solid #333',
        fontWeight: bold ? 800 : 700,
        color: NAVY,
      }}>{right != null ? fmt(right) : ''}</td>
    </tr>
  );
}

// ─── Inline styles ────────────────────────────────────────────────────────
const tableStyle = {
  width: '100%', borderCollapse: 'collapse', marginTop: 18,
  fontFamily: 'Helvetica Neue, -apple-system, Segoe UI, Roboto, Arial, sans-serif',
};
const thStyle = {
  textAlign: 'right', fontSize: 11.5, letterSpacing: '0.08em',
  color: RED, padding: '6px 12px', fontWeight: 800,
};
const itemStyle = {
  padding: '5px 12px', fontSize: 13, color: '#1e293b', paddingLeft: 24,
};
const amountStyle = {
  padding: '5px 12px', fontSize: 13, textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  color: '#1e293b', minWidth: 120,
};
const headingCellStyle = {
  padding: '10px 12px 4px', fontSize: 13.5, fontWeight: 800, color: NAVY,
};
const bandStyle = {
  padding: '6px 12px', fontSize: 12.5, fontWeight: 700, color: NAVY,
  background: '#F5EFE4', textTransform: 'uppercase', letterSpacing: '0.06em',
};
